import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from "uuid";
import { HttpMonitoringApi } from "../../interface/monitoring-api";
import { normalizeExcludeTerms } from "./monitoringExcludeTermUtils";

type ActiveKeywordType = "SEARCH" | "NEGATIVE";
type KeywordType = ActiveKeywordType | "POSITIVE";
type ConfigTab = ActiveKeywordType | "PROMPT";
type KeywordStatusFilter = "ALL" | "ENABLED" | "DISABLED";

interface KeywordItem {
  pk: string;
  sk: string;
  keyword_id?: string;
  created_at?: string;
  kind?: string;
  type: ActiveKeywordType;
  value: string;
  enabled: boolean;
  display_order: number;
  excludeTerms: string[];
}

interface Draft {
  type: ActiveKeywordType;
  value: string;
  enabled: boolean;
  excludeTerms: string[];
}

interface SentimentPromptItem {
  promptId?: string;
  instruction: string;
  versionSk?: string | null;
  source?: string;
  changeNote?: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

const monitoringApi = new HttpMonitoringApi();

const dropdownChevronIcon = "/assets/keyword-config/dropdown-chevron.png";
const keywordAddIcon = "/assets/keyword-config/keyword-add.png";
const monitoringListSearchIcon = `${process.env.PUBLIC_URL}/assets/monitoring-list/icon-search.png`;
const negativeKeywordIcon = "/assets/keyword-config/negative-keyword.png";
const promptExpandCollapseIcon = "/assets/keyword-config/prompt-expand-collapse.png";
const promptTerminalIcon = "/assets/keyword-config/prompt-terminal.png";
const searchKeywordIcon = "/assets/keyword-config/search-keyword.png";

const assetIconSx = (size: number) => ({ width: size, height: size, display: "block", objectFit: "contain" });
const renderAssetIcon = (src: string, size: number) => <Box component="img" src={src} alt="" aria-hidden="true" sx={assetIconSx(size)} />;
type DropdownSelectIconProps = React.ImgHTMLAttributes<HTMLImageElement> & { ownerState?: unknown };
const DropdownSelectIcon = ({ ownerState, style, ...props }: DropdownSelectIconProps) => {
  void ownerState;
  return (
    <img
      {...props}
      src={dropdownChevronIcon}
      alt=""
      aria-hidden="true"
      style={{ ...style, width: 12, height: 12, objectFit: "contain", pointerEvents: "none" }}
    />
  );
};

const EmptyRadioIcon = () => <Box component="span" sx={radioEmptyIconSx} />;

const CheckedRadioIcon = () => (
  <Box component="span" sx={radioCheckedIconSx}>
    <Box component="span" sx={radioCheckedDotSx} />
  </Box>
);

const ChipDeleteIcon = (props: React.HTMLAttributes<HTMLSpanElement>) => (
  <Box component="span" {...props} sx={chipDeleteIconSx}>
    <Box component="span" sx={chipDeleteLineSx(45)} />
    <Box component="span" sx={chipDeleteLineSx(-45)} />
  </Box>
);

const keywordTypes: Array<{
  type: ActiveKeywordType;
  label: string;
  desc: string;
  icon: React.ReactNode;
}> = [
  { type: "SEARCH", label: "검색 키워드", desc: "기사 수집에 사용하는 검색어", icon: renderAssetIcon(searchKeywordIcon, 20) },
  { type: "NEGATIVE", label: "부정 키워드", desc: "부정 기사 판정에 사용하는 전역 키워드", icon: renderAssetIcon(negativeKeywordIcon, 24) },
];

const configTabs: Array<{
  type: ConfigTab;
  label: string;
  desc: string;
  icon: React.ReactNode;
}> = [
  ...keywordTypes,
  { type: "PROMPT", label: "판정 프롬프트", desc: "LLM 긍부정 판정 지침 본문", icon: renderAssetIcon(promptTerminalIcon, 22) },
];

const statusFilterOptions: Array<{ value: KeywordStatusFilter; label: string }> = [
  { value: "ALL", label: "전체" },
  { value: "ENABLED", label: "활성" },
  { value: "DISABLED", label: "비활성" },
];

const emptyDraft: Draft = {
  type: "SEARCH",
  value: "",
  enabled: true,
  excludeTerms: [],
};

// 레거시 검색어 형식과의 호환을 위해 생성 시각 기반 식별 문자열을 만든다.
const makeTimestamp = () => {
  const now = new Date();
  const pad = (value: number, size = 2) => String(value).padStart(size, "0");
  return [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
    pad(now.getMilliseconds() * 1000, 6),
  ].join("");
};

// API 응답 또는 기존 화면 데이터의 여러 필드명을 해석한다. 레거시 POSITIVE는 화면 상태에서 숨긴다.
const resolveType = (item: any): KeywordType => {
  if (item.type === "SEARCH" || item.type === "POSITIVE" || item.type === "NEGATIVE") return item.type;
  if (item.kind === "POSITIVE_SIGNAL" || item.pk === "signal_positive" || item.sentiment === "POSITIVE") return "POSITIVE";
  if (item.kind === "NEGATIVE_SIGNAL" || item.pk === "signal_negative" || item.sentiment === "NEGATIVE") return "NEGATIVE";
  return "SEARCH";
};

// 타입별 표시 라벨, 설명, 색상 메타데이터를 찾는다.
const getTypeMeta = (type: ActiveKeywordType) => keywordTypes.find((item) => item.type === type) ?? keywordTypes[0];

// DynamoDB 단일 테이블 키 규칙에 맞춰 키워드 설정 PK를 만든다.
const getPk = (type: ActiveKeywordType, id = uuidv4()) => {
  if (type === "SEARCH") return `SEARCH_TERM#${id}`;
  return `SENTIMENT_KEYWORD#${id}`;
};

// 기존 item의 keyword_id 또는 PK에서 타입 변경 후에도 유지할 식별자를 추출한다.
const getKeywordId = (item: KeywordItem) => item.keyword_id || (item.pk.includes("#") ? item.pk.split("#", 2)[1] : uuidv4());

// 기존 화면에서 사용하던 kind 값을 새 타입 값에서 역산한다.
const getKind = (type: ActiveKeywordType) => {
  if (type === "NEGATIVE") return "NEGATIVE_SIGNAL";
  return "SEARCH_KEYWORD";
};

// 기존 데이터와 신규 META item이 함께 표시될 수 있도록 화면용 SK 값을 보정한다.
const makeSk = (type: ActiveKeywordType, value: string, id: string) => {
  if (type === "SEARCH") return `keyword#${id}`;
  return value;
};

// API 응답 item을 키워드 설정 화면에서 사용하는 KeywordItem 형태로 변환한다.
const normalizeItem = (item: any, type: ActiveKeywordType): KeywordItem => {
  const value = item.value || item.keyword || item.sk || "";
  return {
    pk: item.pk || getPk(type, item.keyword_id || item.searchTermId || item.sentimentKeywordId),
    sk: item.sk || makeSk(type, value, item.keyword_id || uuidv4()),
    keyword_id: item.keyword_id || item.searchTermId || item.sentimentKeywordId,
    created_at: item.created_at || item.createdAt,
    kind: item.kind || getKind(type),
    type,
    value,
    enabled: item.enabled !== false,
    display_order: Number(item.display_order ?? item.displayOrder ?? 0),
    excludeTerms: type === "SEARCH" ? normalizeExcludeTerms(item.excludeTerms) : [],
  };
};

const normalizeActiveItems = (items: any[]): KeywordItem[] =>
  items.reduce<KeywordItem[]>((acc, item) => {
    const type = resolveType(item);
    if (type !== "POSITIVE") acc.push(normalizeItem(item, type));
    return acc;
  }, []);

const keywordOperationKey = (item: KeywordItem) => `keyword:${item.pk}:${item.sk}`;
const keywordCreateOperationKey = (type: ActiveKeywordType) => `keyword:create:${type}`;
const promptRestoreOperationKey = (versionSk?: string | null) => `prompt:restore:${versionSk ?? "unknown"}`;

const makeKeywordConfigPayload = (item: KeywordItem) => {
  const { excludeTerms, ...payload } = item;
  if (item.type !== "SEARCH") return payload;
  return { ...payload, excludeTerms: normalizeExcludeTerms(excludeTerms) };
};

const getApiErrorMessage = (response: any, fallback: string) => {
  const errorMessage = response?.response?.error_msg || response?.response?.message;
  return errorMessage ? `${fallback}: ${errorMessage}` : fallback;
};

const formatErrors = (errors: string[]) => errors.filter(Boolean).join(" / ");

const KeywordConfigPage = () => {
  const [keywords, setKeywords] = useState<KeywordItem[]>([]);
  const [activeType, setActiveType] = useState<ConfigTab>("SEARCH");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editTarget, setEditTarget] = useState<KeywordItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KeywordItem | null>(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<KeywordStatusFilter>("ALL");
  const [loading, setLoading] = useState(false);
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [promptDraft, setPromptDraft] = useState("");
  const [promptActive, setPromptActive] = useState<SentimentPromptItem | null>(null);
  const [promptHistory, setPromptHistory] = useState<SentimentPromptItem[]>([]);
  const [promptChangeNote, setPromptChangeNote] = useState("");
  const [promptConstraints, setPromptConstraints] = useState({ minLength: 100, maxLength: 8000 });
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [promptDialogTarget, setPromptDialogTarget] = useState<SentimentPromptItem | null>(null);

  // 프롬프트 설정 응답을 활성 본문, 제약 조건, 이력 상태로 나눠 반영한다.
  const applyPromptResponse = useCallback((response: any) => {
    const activePrompt = response?.activePrompt ?? {};
    const history = Array.isArray(response?.history) ? response.history : [];
    const constraints = response?.constraints ?? {};
    setPromptActive(activePrompt);
    setPromptDraft(activePrompt.instruction ?? "");
    setPromptHistory(history);
    setPromptConstraints({
      minLength: Number(constraints.minLength ?? 100),
      maxLength: Number(constraints.maxLength ?? 8000),
    });
    setPromptChangeNote("");
  }, []);

  const isSavingKey = useCallback((key: string) => Boolean(savingKeys[key]), [savingKeys]);
  const saving = useMemo(() => Object.values(savingKeys).some(Boolean), [savingKeys]);
  const promptSaving = useMemo(() => Object.keys(savingKeys).some((key) => key.startsWith("prompt:")), [savingKeys]);

  const runWithSaving = async (key: string, action: () => Promise<void>) => {
    setSavingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      await action();
    } finally {
      setSavingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const loadKeywordConfig = useCallback(async (fallbackError?: string | null) => {
    setLoading(true);
    if (!fallbackError) setLoadError(null);
    try {
      const response = await monitoringApi.post({ command: "get_keyword_config_data" });
      if (response?.code === "200") {
        setKeywords(normalizeActiveItems((response.response?.keywords ?? []) as any[]));
        setLoadError(fallbackError ?? null);
      } else {
        const errors = [fallbackError ?? "", getApiErrorMessage(response, "키워드 영역 목록 다시 불러오기 실패")];
        setLoadError(formatErrors(errors));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "알 수 없는 오류";
      const errors = [fallbackError ?? "", `키워드 영역 목록 다시 불러오기 실패: ${message}`];
      setLoadError(formatErrors(errors));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPromptConfig = useCallback(async (fallbackError?: string | null) => {
    setLoading(true);
    if (!fallbackError) setLoadError(null);
    try {
      const response = await monitoringApi.post({ command: "get_sentiment_prompt_config" });
      if (response?.code === "200") {
        applyPromptResponse(response.response);
        setLoadError(fallbackError ?? null);
      } else {
        const errors = [fallbackError ?? "", getApiErrorMessage(response, "판정 프롬프트 영역 다시 불러오기 실패")];
        setLoadError(formatErrors(errors));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "알 수 없는 오류";
      const errors = [fallbackError ?? "", `판정 프롬프트 영역 다시 불러오기 실패: ${message}`];
      setLoadError(formatErrors(errors));
    } finally {
      setLoading(false);
    }
  }, [applyPromptResponse]);

  const loadConfig = useCallback(
    async (ignore?: () => boolean) => {
      setLoading(true);
      setLoadError(null);
      try {
        const [response, promptResponse] = await Promise.all([
          monitoringApi.post({ command: "get_keyword_config_data" }),
          monitoringApi.post({ command: "get_sentiment_prompt_config" }),
        ]);
        if (ignore?.()) return;
        const errors: string[] = [];
        if (response?.code === "200") {
          setKeywords(normalizeActiveItems((response.response?.keywords ?? []) as any[]));
        } else {
          errors.push(getApiErrorMessage(response, "키워드 영역 설정 불러오기 실패"));
        }
        if (promptResponse?.code === "200") {
          applyPromptResponse(promptResponse.response);
        } else {
          errors.push(getApiErrorMessage(promptResponse, "판정 프롬프트 영역 설정 불러오기 실패"));
        }
        setLoadError(errors.length ? formatErrors(errors) : null);
      } catch (error) {
        if (!ignore?.()) {
          const message = error instanceof Error ? error.message : "알 수 없는 오류";
          setLoadError(`설정 불러오기 실패: ${message}`);
        }
      } finally {
        if (!ignore?.()) setLoading(false);
      }
    },
    [applyPromptResponse]
  );

  useEffect(() => {
    let ignore = false;
    loadConfig(() => ignore);
    return () => {
      ignore = true;
    };
  }, [loadConfig]);

  // 현재 탭과 검색 필터에 맞는 키워드 목록을 화면 표시 순서로 계산한다.
  const visibleKeywords = useMemo(() => {
    if (activeType === "PROMPT") return [];
    const normalizedFilter = filter.trim().toLowerCase();
    return keywords
      .filter((item) => item.type === activeType)
      .filter((item) => statusFilter === "ALL" || (statusFilter === "ENABLED" ? item.enabled : !item.enabled))
      .filter((item) => !normalizedFilter || item.value.toLowerCase().includes(normalizedFilter))
      .sort((a, b) => a.display_order - b.display_order || a.value.localeCompare(b.value));
  }, [activeType, filter, keywords, statusFilter]);

  // 타입별 활성/전체 개수를 계산해 상단 요약 카드에 표시한다.
  const counts = useMemo(() => {
    return keywordTypes.reduce<Record<ActiveKeywordType, { active: number; total: number }>>(
      (acc, item) => {
        const typed = keywords.filter((keyword) => keyword.type === item.type);
        acc[item.type] = { active: typed.filter((keyword) => keyword.enabled).length, total: typed.length };
        return acc;
      },
      {
        SEARCH: { active: 0, total: 0 },
        NEGATIVE: { active: 0, total: 0 },
      }
    );
  }, [keywords]);

  // 판정 프롬프트 지침 본문을 새 버전으로 저장한다.
  const savePrompt = async () => {
    if (loading || promptSaving) return;
    const confirmed = window.confirm("전역 판정 프롬프트를 새 버전으로 저장하시겠습니까?");
    if (!confirmed) return;

    await runWithSaving("prompt:save", async () => {
      setLoadError(null);
      try {
        const response = await monitoringApi.post({
          command: "save_sentiment_prompt_config",
          instruction: promptDraft,
          changeNote: promptChangeNote,
        });
        if (response?.code === "200") {
          applyPromptResponse(response.response);
          return;
        }
        const message = getApiErrorMessage(response, "판정 프롬프트 영역 저장 실패");
        setLoadError(message);
        await loadPromptConfig(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : "알 수 없는 오류";
        const fallback = `판정 프롬프트 영역 저장 실패: ${message}`;
        setLoadError(fallback);
        await loadPromptConfig(fallback);
      }
    });
  };

  // 선택한 과거 버전을 현재 활성 프롬프트로 복원한다.
  const restorePrompt = async (prompt: SentimentPromptItem) => {
    const versionSk = prompt.versionSk;
    if (!versionSk || loading || promptSaving) return;

    await runWithSaving(promptRestoreOperationKey(versionSk), async () => {
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "restore_sentiment_prompt_config", versionSk });
        if (response?.code === "200") {
          applyPromptResponse(response.response);
          setPromptDialogTarget(null);
          return;
        }
        const message = getApiErrorMessage(response, "판정 프롬프트 영역 복원 실패");
        setLoadError(message);
        await loadPromptConfig(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : "알 수 없는 오류";
        const fallback = `판정 프롬프트 영역 복원 실패: ${message}`;
        setLoadError(fallback);
        await loadPromptConfig(fallback);
      }
    });
  };

  // 선택한 키워드 타입으로 신규 생성 다이얼로그를 연다.
  const openCreate = (type: ActiveKeywordType) => {
    setDraft({ ...emptyDraft, type });
    setDialogOpen(true);
  };

  // 다이얼로그 입력값을 신규 KeywordItem 형태로 조립한다.
  const makeItemFromDraft = (sourceDraft: Draft) => {
    const value = sourceDraft.value.trim();
    const keywordId = uuidv4();
    return {
      pk: getPk(sourceDraft.type, keywordId),
      sk: "META",
      keyword_id: sourceDraft.type === "SEARCH" ? keywordId : undefined,
      created_at: sourceDraft.type === "SEARCH" ? makeTimestamp() : undefined,
      kind: getKind(sourceDraft.type),
      type: sourceDraft.type,
      value,
      enabled: sourceDraft.enabled,
      display_order: Math.max(0, ...keywords.filter((item) => item.type === sourceDraft.type).map((item) => item.display_order)) + 1,
      excludeTerms: sourceDraft.type === "SEARCH" ? normalizeExcludeTerms(sourceDraft.excludeTerms) : [],
    };
  };

  // 신규 키워드는 API 저장 성공 후 목록을 다시 불러와 화면에 반영한다.
  const handleCreate = async (submittedDraft: Draft) => {
    if (!submittedDraft.value.trim() || loading) return;
    const item = makeItemFromDraft(submittedDraft);
    const requestItem = makeKeywordConfigPayload(item);
    await runWithSaving(keywordCreateOperationKey(item.type), async () => {
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "put_keyword_config_item", item: requestItem });
        if (response?.code === "200") {
          setDialogOpen(false);
          await loadKeywordConfig();
          return;
        }
        const message = getApiErrorMessage(response, `${getTypeMeta(item.type).label} 추가 실패`);
        setLoadError(message);
        await loadKeywordConfig(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : "알 수 없는 오류";
        const fallback = `${getTypeMeta(item.type).label} 추가 실패: ${message}`;
        setLoadError(fallback);
        await loadKeywordConfig(fallback);
      }
    });
  };

  // 수정 내용은 API 저장 성공 후 목록을 다시 불러와 화면에 반영한다.
  const handleEditSave = async (submittedDraft: Draft) => {
    if (!editTarget || loading) return;
    const keywordId = getKeywordId(editTarget);
    const nextItem = {
      ...editTarget,
      pk: getPk(submittedDraft.type, keywordId),
      sk: "META",
      keyword_id: keywordId,
      kind: getKind(submittedDraft.type),
      type: submittedDraft.type,
      value: submittedDraft.value.trim(),
      enabled: submittedDraft.enabled,
      excludeTerms: submittedDraft.type === "SEARCH" ? normalizeExcludeTerms(submittedDraft.excludeTerms) : [],
    };
    const requestItem = { ...makeKeywordConfigPayload(nextItem), previousPk: editTarget.pk, previousSk: editTarget.sk };
    await runWithSaving(keywordOperationKey(editTarget), async () => {
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "put_keyword_config_item", item: requestItem });
        if (response?.code === "200") {
          setEditTarget(null);
          setActiveType(nextItem.type);
          await loadKeywordConfig();
          return;
        }
        const message = getApiErrorMessage(response, `${getTypeMeta(editTarget.type).label} 수정 실패`);
        setLoadError(message);
        await loadKeywordConfig(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : "알 수 없는 오류";
        const fallback = `${getTypeMeta(editTarget.type).label} 수정 실패: ${message}`;
        setLoadError(fallback);
        await loadKeywordConfig(fallback);
      }
    });
  };

  // 키워드 활성/비활성 토글은 API 저장 성공 후 목록을 다시 불러와 화면에 반영한다.
  const toggleEnabled = async (target: KeywordItem) => {
    if (loading || isSavingKey(keywordOperationKey(target))) return;
    const nextItem = { ...target, enabled: !target.enabled };
    const requestItem = makeKeywordConfigPayload(nextItem);
    await runWithSaving(keywordOperationKey(target), async () => {
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "put_keyword_config_item", item: requestItem });
        if (response?.code === "200") {
          await loadKeywordConfig();
          return;
        }
        const message = getApiErrorMessage(response, `${getTypeMeta(target.type).label} 상태 변경 실패`);
        setLoadError(message);
        await loadKeywordConfig(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : "알 수 없는 오류";
        const fallback = `${getTypeMeta(target.type).label} 상태 변경 실패: ${message}`;
        setLoadError(fallback);
        await loadKeywordConfig(fallback);
      }
    });
  };

  // 삭제 대상은 API 삭제 성공 후 목록을 다시 불러와 화면에서 제거한다.
  const handleDelete = async () => {
    if (!deleteTarget || loading) return;
    const target = deleteTarget;
    await runWithSaving(keywordOperationKey(target), async () => {
      setLoadError(null);
      try {
        const response = await monitoringApi.post({ command: "delete_keyword_config_item", pk: target.pk, sk: target.sk });
        if (response?.code === "200") {
          setDeleteTarget(null);
          await loadKeywordConfig();
          return;
        }
        const message = getApiErrorMessage(response, `${getTypeMeta(target.type).label} 삭제 실패`);
        setLoadError(message);
        await loadKeywordConfig(message);
      } catch (error) {
        const message = error instanceof Error ? error.message : "알 수 없는 오류";
        const fallback = `${getTypeMeta(target.type).label} 삭제 실패: ${message}`;
        setLoadError(fallback);
        await loadKeywordConfig(fallback);
      }
    });
  };

  // 선택한 키워드의 현재 값을 편집 다이얼로그 draft로 복사한다.
  const openEdit = (item: KeywordItem) => {
    setEditTarget(item);
    setDraft({
      type: item.type,
      value: item.value,
      enabled: item.enabled,
      excludeTerms: item.type === "SEARCH" ? normalizeExcludeTerms(item.excludeTerms) : [],
    });
  };

  const createSaving = isSavingKey(keywordCreateOperationKey(draft.type));
  const editSaving = editTarget ? isSavingKey(keywordOperationKey(editTarget)) : false;
  const deleteSaving = deleteTarget ? isSavingKey(keywordOperationKey(deleteTarget)) : false;
  const openCreateFromCurrentTab = () => openCreate(activeType === "NEGATIVE" ? "NEGATIVE" : "SEARCH");

  return (
    <Box sx={pageSx}>
      <Stack spacing={0}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: "stretch", md: "flex-end" }}>
          <Box>
            <Typography sx={pageTitleSx}>키워드 관리</Typography>
            <Typography sx={{ ...pageSubtitleSx, color: loadError ? "#b91c1c" : "#2E3341" }}>
              {loading ? "키워드 설정을 불러오는 중입니다." : loadError ?? (saving ? "변경 내용을 저장하는 중입니다." : "검색 키워드, 부정 판정 키워드, LLM 판정 지침을 전역으로 관리합니다.")}
            </Typography>
          </Box>
          <Button startIcon={renderAssetIcon(keywordAddIcon, 16)} onClick={openCreateFromCurrentTab} disabled={loading || isSavingKey(keywordCreateOperationKey(activeType === "NEGATIVE" ? "NEGATIVE" : "SEARCH"))} sx={topAddButtonSx}>
            키워드 추가
          </Button>
        </Stack>

        <Box sx={summaryGridSx}>
          {configTabs.map((item) => (
            <MetricCard
              key={item.type}
              active={activeType === item.type}
              icon={item.icon}
              label={item.label}
              desc={item.desc}
              value={item.type === "PROMPT" ? `${promptHistory.length}개` : `${counts[item.type].active}개`}
              caption={item.type === "PROMPT" ? "" : `/ 전체 ${counts[item.type].total}개`}
              tone={item.type}
              onClick={() => setActiveType(item.type)}
            />
          ))}
        </Box>

        {activeType === "PROMPT" ? (
          <PromptConfigPanel
            activePrompt={promptActive}
            draft={promptDraft}
            changeNote={promptChangeNote}
            history={promptHistory}
            constraints={promptConstraints}
            loading={loading}
            promptSaving={promptSaving}
            expanded={promptExpanded}
            onExpandedChange={setPromptExpanded}
            onDraftChange={setPromptDraft}
            onChangeNoteChange={setPromptChangeNote}
            onSave={savePrompt}
            onViewPrompt={setPromptDialogTarget}
            isRestoring={(versionSk) => isSavingKey(promptRestoreOperationKey(versionSk))}
          />
        ) : (
          <Paper variant="outlined" sx={panelSx}>
            <Stack direction="row" spacing={1} sx={listHeaderSx}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={cardTitleSx}>{getTypeMeta(activeType).label} 목록</Typography>
                <Chip size="small" label={`${counts[activeType].total}건`} sx={listCountChipSx} />
              </Stack>
            </Stack>
            <Stack sx={listBodySx}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={filterBarSx}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={filterLabelSx}>상태</Typography>
                  <TextField
                    select
                    size="small"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as KeywordStatusFilter)}
                    sx={statusSelectSx}
                    inputProps={{ "aria-label": "상태" }}
                    SelectProps={{ IconComponent: DropdownSelectIcon }}
                  >
                    {statusFilterOptions.map((item) => (
                      <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  size="small"
                  placeholder="키워드명 검색"
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  sx={searchFieldSx}
                  inputProps={{ "aria-label": "키워드명 검색" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box component="img" src={monitoringListSearchIcon} alt="" aria-hidden="true" sx={searchIconSx} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Box sx={keywordTableSx}>
                <Box sx={keywordTableHeadSx}>
                  <TableHead>종류</TableHead>
                  <TableHead>{activeType === "SEARCH" ? "모니터링" : "키워드"}</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>관리</TableHead>
                </Box>
                <Box sx={keywordTableBodySx}>
                  {visibleKeywords.length === 0 ? (
                    <Typography sx={emptySx}>표시할 키워드가 없습니다.</Typography>
                  ) : (
                    visibleKeywords.map((item) => (
                      <KeywordRow
                        key={`${item.pk}-${item.sk}`}
                        item={item}
                        disabled={loading || isSavingKey(keywordOperationKey(item))}
                        onToggle={() => toggleEnabled(item)}
                        onEdit={() => openEdit(item)}
                        onDelete={() => setDeleteTarget(item)}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>

      <KeywordDialog
        open={dialogOpen}
        draft={draft}
        onDraftChange={setDraft}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreate}
        disabled={loading || createSaving}
        submitting={createSaving}
      />

      <KeywordDialog
        open={!!editTarget}
        draft={draft}
        onDraftChange={setDraft}
        onClose={() => setEditTarget(null)}
        onSubmit={handleEditSave}
        disabled={loading || editSaving}
        submitting={editSaving}
        editMode
      />

      <Dialog open={!!deleteTarget} onClose={loading || deleteSaving ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "10px" } }}>
        <DialogTitle sx={dialogTitleSx}>
          <Typography sx={dialogTitleTextSx}>키워드 삭제</Typography>
          <IconButton size="small" onClick={() => setDeleteTarget(null)} disabled={loading || deleteSaving}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Typography sx={{ color: "#4a5568", fontSize: 13, lineHeight: 1.6 }}>
            "{deleteTarget?.value}" 키워드를 삭제하시겠습니까?
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setDeleteTarget(null)} disabled={loading || deleteSaving} sx={outlineButtonSx}>취소</Button>
            <Button onClick={handleDelete} variant="contained" disabled={loading || deleteSaving} sx={dangerButtonSx}>{deleteSaving ? "삭제 중" : "삭제"}</Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <PromptHistoryDialog
        prompt={promptDialogTarget}
        activeVersionSk={promptActive?.versionSk}
        open={!!promptDialogTarget}
        disabled={loading || promptSaving}
        restoring={isSavingKey(promptRestoreOperationKey(promptDialogTarget?.versionSk))}
        onClose={() => setPromptDialogTarget(null)}
        onRestore={restorePrompt}
      />
    </Box>
  );
};

const KeywordDialog = ({
  open,
  draft,
  onDraftChange,
  onClose,
  onSubmit,
  disabled,
  submitting,
  editMode,
}: {
  open: boolean;
  draft: Draft;
  onDraftChange: (draft: Draft) => void;
  onClose: () => void;
  onSubmit: (draft: Draft) => void;
  disabled?: boolean;
  submitting?: boolean;
  editMode?: boolean;
}) => {
  const [excludeInput, setExcludeInput] = useState("");
  const excludeTerms = normalizeExcludeTerms(draft.excludeTerms);
  const canAddExcludeTerm = normalizeExcludeTerms(excludeInput).length > 0;

  useEffect(() => {
    if (!open || draft.type !== "SEARCH") setExcludeInput("");
  }, [draft.type, open]);

  const handleKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing || disabled || !draft.value.trim()) return;
    event.preventDefault();
    submitDraft();
  };

  const addExcludeInput = () => {
    const nextTerms = normalizeExcludeTerms(excludeInput);
    if (!nextTerms.length || disabled) return;
    onDraftChange({ ...draft, excludeTerms: normalizeExcludeTerms([...excludeTerms, ...nextTerms]) });
    setExcludeInput("");
  };

  const removeExcludeTerm = (index: number) => {
    if (disabled) return;
    onDraftChange({ ...draft, excludeTerms: normalizeExcludeTerms(excludeTerms.filter((_term, currentIndex) => currentIndex !== index)) });
  };

  const handleExcludeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    addExcludeInput();
  };

  const buildSubmittedDraft = () => {
    if (draft.type !== "SEARCH") return { ...draft, excludeTerms: [] };
    return { ...draft, excludeTerms: normalizeExcludeTerms([...excludeTerms, excludeInput]) };
  };

  const submitDraft = () => {
    const submittedDraft = buildSubmittedDraft();
    if (draft.type === "SEARCH") {
      onDraftChange(submittedDraft);
      setExcludeInput("");
    }
    onSubmit(submittedDraft);
  };

  return (
    <Dialog open={open} onClose={disabled ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: keywordDialogPaperSx }}>
      <DialogTitle sx={keywordDialogTitleSx}>{editMode ? "키워드 정보 수정" : "키워드 추가"}</DialogTitle>
      <DialogContent sx={keywordDialogContentSx}>
          <Box sx={keywordDialogFieldGroupSx}>
            <Typography sx={keywordDialogFieldLabelSx}>키워드 구분</Typography>
            <RadioGroup
              row
              aria-label="키워드 구분"
              value={draft.type}
              onChange={(event) => onDraftChange({ ...draft, type: event.target.value as ActiveKeywordType })}
              sx={keywordDialogRadioGroupSx}
            >
              {keywordTypes.map((item) => (
                <FormControlLabel
                  key={item.type}
                  value={item.type}
                  disabled={disabled}
                  control={<Radio size="small" icon={<EmptyRadioIcon />} checkedIcon={<CheckedRadioIcon />} sx={figmaRadioSx} />}
                  label={item.label}
                  sx={keywordDialogRadioLabelSx}
                />
              ))}
            </RadioGroup>
          </Box>
          <Box sx={keywordDialogFieldGroupSx}>
            <Typography sx={keywordDialogFieldLabelSx}>추가할 키워드</Typography>
            <TextField
              autoFocus
              size="small"
              value={draft.value}
              onChange={(event) => onDraftChange({ ...draft, value: event.target.value })}
              onKeyDown={handleKeywordKeyDown}
              disabled={disabled}
              inputProps={{ "aria-label": "키워드" }}
              sx={keywordDialogTextFieldSx}
            />
          </Box>
          {draft.type === "SEARCH" && (
            <Box sx={keywordDialogExcludeSectionSx}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={keywordDialogExcludeHeaderSx}>
                <Typography sx={keywordDialogFieldLabelSx}>제외 키워드</Typography>
                <Box sx={keywordDialogCountTagSx}>{excludeTerms.length}개</Box>
              </Stack>
              <Stack direction="row" sx={keywordDialogExcludeInputRowSx}>
                <TextField
                  multiline
                  minRows={1}
                  maxRows={1}
                  size="small"
                  value={excludeInput}
                  onChange={(event) => setExcludeInput(event.target.value)}
                  onKeyDown={handleExcludeKeyDown}
                  disabled={disabled}
                  placeholder="쉼표, 줄바꿈 또는 Enter로 추가"
                  inputProps={{ "aria-label": "제외 키워드" }}
                  sx={keywordDialogTextFieldSx}
                />
                <Button
                  startIcon={<AddIcon />}
                  aria-label="제외 키워드 추가"
                  sx={keywordDialogExcludeAddButtonSx}
                  disabled={!canAddExcludeTerm || disabled}
                  onClick={addExcludeInput}
                >
                  추가
                </Button>
              </Stack>
              <Box sx={keywordDialogExcludeWellSx(excludeTerms.length > 0)}>
                {excludeTerms.length ? (
                  <Stack direction="row" sx={keywordDialogExcludeChipListSx}>
                    {excludeTerms.map((term, index) => (
                      <Chip
                        key={`${term}-${index}`}
                        label={term}
                        disabled={disabled}
                        onDelete={() => removeExcludeTerm(index)}
                        deleteIcon={<ChipDeleteIcon aria-label={`${term} 제외 키워드 삭제`} />}
                        sx={keywordDialogExcludeChipSx}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={keywordDialogExcludeEmptySx}>제외 키워드가 없습니다</Typography>
                )}
              </Box>
            </Box>
          )}
          <Stack direction="row" alignItems="center" sx={keywordDialogEnabledRowSx}>
            <Typography sx={draft.enabled ? keywordDialogEnabledLabelSx : keywordDialogDisabledLabelSx}>
              {draft.enabled ? "활성" : "비활성"}
            </Typography>
            <Switch checked={draft.enabled} onChange={(event) => onDraftChange({ ...draft, enabled: event.target.checked })} disabled={disabled} sx={keywordSwitchSx} />
          </Stack>
      </DialogContent>
      <DialogActions sx={keywordDialogActionsSx}>
        <Button onClick={onClose} disabled={disabled} sx={keywordDialogCancelButtonSx}>취소</Button>
        <Button onClick={submitDraft} variant="contained" disabled={!draft.value.trim() || disabled} sx={keywordDialogPrimaryButtonSx(editMode)}>
          {submitting ? (editMode ? "저장 중" : "추가 중") : editMode ? "저장" : "키워드 추가"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const KeywordRow = ({ item, disabled, onToggle, onEdit, onDelete }: { item: KeywordItem; disabled?: boolean; onToggle: () => void; onEdit: () => void; onDelete: () => void }) => {
  const excludeTerms = item.type === "SEARCH" ? normalizeExcludeTerms(item.excludeTerms) : [];
  return (
    <Box sx={keywordRowSx(item.enabled)}>
      <Chip label={getTypeMeta(item.type).label} size="small" sx={typeChipSx(item.type)} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={keywordValueSx}>{item.value}</Typography>
        {item.type === "SEARCH" && <KeywordExcludeSummary excludeTerms={excludeTerms} />}
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        <Typography sx={{ color: item.enabled ? "#2E3341" : "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "22px", letterSpacing: "-0.015em" }}>{item.enabled ? "활성" : "비활성"}</Typography>
        <Switch checked={item.enabled} size="small" onChange={onToggle} disabled={disabled} inputProps={{ "aria-label": `${item.value} 상태 변경` }} sx={keywordSwitchSx} />
      </Stack>
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title="수정">
          <span>
            <Button size="small" aria-label={`${item.value} 수정`} onClick={onEdit} disabled={disabled} sx={rowEditButtonSx}>
              수정
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="삭제">
          <span>
            <Button size="small" aria-label={`${item.value} 삭제`} onClick={onDelete} disabled={disabled} sx={rowDeleteButtonSx}>
              삭제
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const KeywordExcludeSummary = ({ excludeTerms }: { excludeTerms: string[] }) => {
  const visibleTerms = excludeTerms.slice(0, 3);
  const hiddenCount = Math.max(0, excludeTerms.length - visibleTerms.length);
  return (
    <Stack direction="row" spacing={0.45} sx={keywordExcludeSummarySx}>
      <Typography sx={keywordExcludeLabelSx}>제외</Typography>
      {visibleTerms.length ? (
        <>
          {visibleTerms.map((term) => <Chip key={term} label={term} size="small" sx={rowExcludeTermChipSx} />)}
          {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} size="small" sx={rowExcludeMoreChipSx} />}
        </>
      ) : (
        <Typography sx={keywordExcludeEmptySx}>없음</Typography>
      )}
    </Stack>
  );
};

const PromptConfigPanel = ({
  activePrompt,
  draft,
  changeNote,
  history,
  constraints,
  loading,
  promptSaving,
  expanded,
  onExpandedChange,
  onDraftChange,
  onChangeNoteChange,
  onSave,
  onViewPrompt,
  isRestoring,
}: {
  activePrompt: SentimentPromptItem | null;
  draft: string;
  changeNote: string;
  history: SentimentPromptItem[];
  constraints: { minLength: number; maxLength: number };
  loading: boolean;
  promptSaving: boolean;
  expanded: boolean;
  onExpandedChange: (value: boolean) => void;
  onDraftChange: (value: string) => void;
  onChangeNoteChange: (value: string) => void;
  onSave: () => void;
  onViewPrompt: (prompt: SentimentPromptItem) => void;
  isRestoring: (versionSk?: string | null) => boolean;
}) => {
  const characterCount = draft.trim().length;
  const canSave = characterCount >= constraints.minLength && characterCount <= constraints.maxLength;
  return (
    <Stack sx={promptPanelsStackSx}>
      <Paper variant="outlined" sx={panelSx}>
        <Stack direction={{ xs: "column", sm: "row" }} sx={promptPanelHeaderSx}>
          <Typography sx={cardTitleSx}>판정 프롬프트</Typography>
          <Typography sx={promptUpdatedSx}>
            {activePrompt?.updatedAt ? `최근 수정 ${activePrompt.updatedAt}` : "아직 저장된 사용자 프롬프트가 없습니다."}
          </Typography>
        </Stack>
        <Stack sx={promptPanelContentSx}>
          <Box sx={promptEditorGroupSx}>
            <Box sx={promptInstructionGroupSx}>
              <Typography sx={promptFieldLabelSx}>판정 지침 작성</Typography>
              <Box sx={promptEditorShellSx}>
                <TextField
                  multiline
                  minRows={expanded ? 30 : 1}
                  maxRows={expanded ? undefined : 1}
                  value={draft}
                  onChange={(event) => onDraftChange(event.target.value)}
                  disabled={loading || promptSaving}
                  sx={promptTextFieldSx(expanded)}
                  inputProps={{ "aria-label": "판정 지침 본문" }}
                />
                <Button
                  size="small"
                  startIcon={<Box component="img" src={promptExpandCollapseIcon} alt="" aria-hidden="true" sx={promptExpandIconSx} />}
                  onClick={() => onExpandedChange(!expanded)}
                  sx={promptExpandButtonSx}
                >
                  {expanded ? "축소" : "확장"}
                </Button>
              </Box>
            </Box>
            <Typography sx={promptCounterSx}>
              <Box component="span" sx={promptCounterCurrentSx}>{characterCount.toLocaleString()}자</Box>
              <Box component="span" sx={promptCounterLimitSx}> / {constraints.minLength.toLocaleString()}~{constraints.maxLength.toLocaleString()}자</Box>
            </Typography>
          </Box>
          <Box sx={promptNoteGroupSx}>
            <Typography sx={promptFieldLabelSx}>변경 메모</Typography>
            <TextField
              size="small"
              value={changeNote}
              onChange={(event) => onChangeNoteChange(event.target.value)}
              disabled={loading || promptSaving}
              placeholder="변경 이력에 남길 메모를 입력해주세요 (예: 비교 보도 판정 기준 보강)"
              sx={promptNoteFieldSx}
              inputProps={{ "aria-label": "변경 메모" }}
            />
          </Box>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
            <Box sx={promptInfoSx}>
              <Box sx={promptInfoIconSx}>i</Box>
              <Typography sx={promptInfoTextSx}>
                저장 후 새로 처리되는 2차 LLM 판정부터 적용됩니다. 이미 판정된 기사는 변경되지 않습니다.
              </Typography>
            </Box>
            <Button onClick={onSave} variant="contained" disabled={!canSave || loading || promptSaving} sx={promptSaveButtonSx}>
              {promptSaving ? "처리 중" : "프롬프트 저장"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={panelSx}>
        <Stack direction="row" sx={promptHistoryHeaderSx}>
          <Typography sx={cardTitleSx}>최근 변경 이력</Typography>
        </Stack>
        <Stack sx={promptHistoryContentSx}>
          {history.length === 0 ? (
            <Typography sx={emptySx}>저장된 변경 이력이 없습니다.</Typography>
          ) : (
            <Box sx={promptHistoryGridSx}>
              {history.map((item) => {
                const restoring = isRestoring(item.versionSk);
                return (
                  <Box key={item.versionSk || item.updatedAt || item.promptId} sx={promptHistoryCardSx}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={promptHistoryTitleSx} noWrap>
                        {item.changeNote || "변경 메모 없음"}
                      </Typography>
                      <Typography sx={promptHistoryMetaSx}>
                        {item.updatedAt || item.createdAt || "-"} · {item.updatedBy || "-"}
                      </Typography>
                    </Box>
                    <Box sx={promptHistoryActionSx}>
                      <Button onClick={() => onViewPrompt(item)} disabled={loading || restoring} sx={promptHistoryButtonSx}>
                        <Box component="span" sx={promptHistoryButtonLabelSx}>
                          {restoring ? "처리 중" : "프롬프트 보기"}
                        </Box>
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

const PromptHistoryDialog = ({
  prompt,
  activeVersionSk,
  open,
  disabled,
  restoring,
  onClose,
  onRestore,
}: {
  prompt: SentimentPromptItem | null;
  activeVersionSk?: string | null;
  open: boolean;
  disabled?: boolean;
  restoring?: boolean;
  onClose: () => void;
  onRestore: (prompt: SentimentPromptItem) => void;
}) => {
  const isActiveVersion = Boolean(prompt?.versionSk && activeVersionSk === prompt.versionSk);
  const titleId = "prompt-history-dialog-title";
  return (
    <Dialog
      open={open}
      onClose={disabled || restoring ? undefined : onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      BackdropProps={{ sx: promptDialogBackdropSx }}
      PaperProps={{ sx: promptDialogPaperSx }}
    >
      <DialogContent sx={promptDialogContentSx}>
        <Box sx={promptDialogHeaderSx}>
          <Box sx={promptDialogTitleBlockSx}>
            <Typography id={titleId} sx={promptDialogTitleSx} noWrap>{prompt?.changeNote || "변경 메모 없음"}</Typography>
            <Typography sx={promptDialogMetaSx} noWrap>{prompt?.updatedAt || prompt?.createdAt || "-"} · {prompt?.updatedBy || "-"}</Typography>
          </Box>
        </Box>
        <Box sx={promptDialogBodyWrapSx}>
          <Box sx={promptDialogBodySx}>
            <Typography component="pre" sx={promptDialogPreSx}>
              {prompt?.instruction || "본문 미리보기 없음"}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" sx={promptDialogActionsSx}>
          <Button onClick={onClose} disabled={disabled || restoring} sx={promptDialogCancelButtonSx}>취소</Button>
          <Button
            onClick={() => prompt && onRestore(prompt)}
            disabled={!prompt?.versionSk || disabled || restoring || isActiveVersion}
            sx={promptDialogRestoreButtonSx}
          >
            {isActiveVersion ? "현재 적용 중" : restoring ? "복원 중" : "이 프롬프트로 복원"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const MetricCard = ({
  active,
  icon,
  label,
  desc,
  value,
  caption,
  tone,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  desc: string;
  value: string;
  caption: string;
  tone: ConfigTab;
  onClick: () => void;
}) => (
  <Paper component="button" type="button" aria-pressed={active} onClick={onClick} variant="outlined" sx={metricCardSx(active)}>
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box sx={{ ...metricIconSx, bgcolor: typeTone[tone].bg, color: typeTone[tone].color }}>{icon}</Box>
      <Stack sx={metricContentSx}>
        <Box>
          <Typography sx={metricLabelSx}>{label}</Typography>
          <Typography sx={metricDescSx}>{desc}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <Typography sx={metricValueSx}>{value}</Typography>
          {caption && <Typography sx={metricCaptionSx}>{caption}</Typography>}
        </Stack>
      </Stack>
    </Stack>
  </Paper>
);

const TableHead = ({ children, align = "center" }: { children: React.ReactNode; align?: "left" | "center" | "right" }) => (
  <Typography sx={{ color: "#2E3341", fontSize: 14, fontWeight: 500, lineHeight: "22px", letterSpacing: "-0.015em", textAlign: align }}>{children}</Typography>
);

const typeTone: Record<ConfigTab, { bg: string; color: string; chipBg: string }> = {
  SEARCH: { bg: "#CFFAFE", color: "#0E7490", chipBg: "#E4FCFE" },
  NEGATIVE: { bg: "#FFE3E2", color: "#D2362F", chipBg: "#FFE3E2" },
  PROMPT: { bg: "#3D3D3D", color: "#fff", chipBg: "#EEEFF0" },
};

const figmaFontFamily = '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans KR", "Apple SD Gothic Neo", Arial, sans-serif';
const pageSx = { minHeight: "100%", bgcolor: "#F2F4F8", pt: { xs: 2, md: 5 }, px: { xs: 2, md: 8 }, pb: { xs: 3, md: 7 }, overflowY: "auto", fontFamily: figmaFontFamily };
const pageTitleSx = { color: "#2E3341", fontSize: 24, fontWeight: 700, lineHeight: "32px", letterSpacing: "-0.015em" };
const pageSubtitleSx = { mt: "4px", color: "#2E3341", fontSize: 16, fontWeight: 400, lineHeight: "24px", letterSpacing: "-0.015em" };
const summaryGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2, mt: { xs: 3, md: "40px" }, mb: { xs: 2, md: "14px" } };
const panelSx = { overflow: "hidden", borderRadius: "12px", border: "0", bgcolor: "#fff", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)" };
const cardTitleSx = { color: "#000", fontFamily: figmaFontFamily, fontSize: 16, fontWeight: 600, lineHeight: "22px" };
const metricCardSx = (active: boolean) => ({
  width: "100%",
  minHeight: 107,
  p: 2,
  borderRadius: "12px",
  border: `1px solid ${active ? "#2E3341" : "transparent"}`,
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
  cursor: "pointer",
  textAlign: "left",
  color: "inherit",
  font: "inherit",
  appearance: "none",
  "&:hover": { borderColor: active ? "#2E3341" : "#DDE0EA" },
});
const metricIconSx = { width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
const metricContentSx = { flex: 1, minWidth: 0, gap: "9px", alignItems: "flex-start" };
const metricLabelSx = { color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "20px" };
const metricDescSx = { color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px" };
const metricValueSx = { color: "#2E3341", fontSize: 20, fontWeight: 700, lineHeight: "26px" };
const metricCaptionSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const listHeaderSx = { px: 3, pt: 3, pb: 1, height: 56, boxSizing: "border-box", alignItems: "center", flexShrink: 0 };
const listBodySx = { p: 3, gap: "12px", alignItems: "stretch", boxSizing: "border-box" };
const listCountChipSx = { height: 24, borderRadius: "16px", bgcolor: "#EEEFF0", color: "#2E3341", fontSize: 13, fontWeight: 500, lineHeight: "13px", "& .MuiChip-label": { px: 1 } };
const filterBarSx = { minHeight: 48, justifyContent: { xs: "flex-start", sm: "flex-end" }, width: "100%" };
const filterLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", whiteSpace: "nowrap" };
const statusSelectSx = { width: { xs: "100%", sm: 160 }, "& .MuiInputBase-root": { height: 48, borderRadius: "8px", fontFamily: figmaFontFamily, fontSize: 14, fontWeight: 400, bgcolor: "#fff" }, "& .MuiSelect-select": { display: "flex", alignItems: "center", height: "100%", px: 2, py: 0, color: "#2E3341", letterSpacing: "-0.015em" }, "& .MuiSelect-icon": { right: 16, top: "calc(50% - 6px)" }, "& fieldset": { borderColor: "#DDE0EA" } };
const keywordTableSx = { width: "100%" };
const keywordTableHeadSx = {
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: "200px minmax(0, 1fr) 160px 160px",
  alignItems: "center",
  height: 40,
  bgcolor: "#F2F5FD",
  borderTop: "1px solid #2E3341",
  "& > *": { px: 2 },
};
const keywordTableBodySx = { width: "100%" };
const keywordRowSx = (enabled: boolean) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "200px minmax(0, 1fr) 160px 160px" },
  gap: { xs: 0.8, md: 0 },
  alignItems: "center",
  minHeight: 56,
  borderBottom: "1px solid #F2F2F2",
  bgcolor: "#fff",
  "& > *": { px: { xs: 0, md: 2 } },
});
const keywordValueSx = { color: "#2E3341", fontSize: 16, fontWeight: 600, lineHeight: "24px", letterSpacing: "-0.015em" };
const smallChipSx = { height: 22, borderRadius: "20px", bgcolor: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 500 };
const typeChipSx = (type: ActiveKeywordType) => ({
  height: 32,
  borderRadius: "16px",
  justifySelf: "center",
  bgcolor: typeTone[type].chipBg,
  color: typeTone[type].color,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5 },
});
const keywordExcludeSummarySx = { mt: 0.55, flexWrap: "wrap", rowGap: 0.45, alignItems: "center", minWidth: 0 };
const keywordExcludeLabelSx = { color: "#94a3b8", fontSize: 10.5, fontWeight: 500, flexShrink: 0 };
const keywordExcludeEmptySx = { color: "#98a2b3", fontSize: 11, fontWeight: 500 };
const rowExcludeTermChipSx = {
  ...smallChipSx,
  height: 20,
  maxWidth: 112,
  bgcolor: "#ecfeff",
  color: "#0e7490",
  fontSize: 10,
  "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
};
const rowExcludeMoreChipSx = { ...rowExcludeTermChipSx, bgcolor: "#f1f5f9", color: "#64748b" };
const emptySx = { m: 1.5, p: 2, color: "#94a3b8", fontSize: 13, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: "8px" };
const primaryButtonSx = { borderRadius: "6px", bgcolor: "#0891b2", color: "#fff", fontSize: 12, fontWeight: 500, boxShadow: "none", "&:hover": { bgcolor: "#0e7490", boxShadow: "none" } };
const actionButtonMetricsSx = { height: 40, px: "16px", gap: "4px", fontSize: 14, lineHeight: "14px" };
const topAddButtonSx = { ...actionButtonMetricsSx, borderRadius: "8px", color: "#2E3341", border: "1px solid #DDE0EA", bgcolor: "#fff", fontWeight: 600, textTransform: "none", width: 132, boxShadow: "none", "& .MuiButton-startIcon": { m: 0 }, "&:hover": { borderColor: "#C6CBD8", bgcolor: "#fff", boxShadow: "none" } };
const outlineButtonSx = { borderRadius: "8px", color: "#2E3341", border: "1px solid #DDE0EA", bgcolor: "#fff", fontSize: 14, fontWeight: 600, lineHeight: "14px", textTransform: "none", minHeight: 32, px: 1.5, "&:hover": { borderColor: "#C6CBD8", bgcolor: "#fff" } };
const dangerButtonSx = { ...primaryButtonSx, bgcolor: "#E32329", borderRadius: "8px", fontSize: 14, fontWeight: 600, lineHeight: "14px", "&:hover": { bgcolor: "#C81F25" } };
const rowEditButtonSx = { ...outlineButtonSx, width: 48, height: 32, minWidth: 48, minHeight: 32, px: 0 };
const rowDeleteButtonSx = { ...dangerButtonSx, width: 48, height: 32, minWidth: 48, minHeight: 32, px: 0 };
const keywordSwitchSx = {
  width: 40,
  height: 24,
  p: 0,
  "& .MuiSwitch-switchBase": {
    p: "4px",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": { bgcolor: "#5D88F1", opacity: 1 },
    },
  },
  "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
  "& .MuiSwitch-track": { borderRadius: "20px", bgcolor: "#D0D0D0", opacity: 1 },
};
const searchFieldSx = { width: { xs: "100%", sm: 400 }, "& .MuiInputBase-root": { height: 48, borderRadius: "8px", fontFamily: figmaFontFamily, fontSize: 16, fontWeight: 400, bgcolor: "#fff" }, "& .MuiInputAdornment-root": { mr: "12px" }, "& input": { color: "#2E3341", letterSpacing: "-0.015em", py: 0 }, "& input::placeholder": { color: "#A3A3A3", opacity: 1 }, "& fieldset": { borderColor: "#DDE0EA" } };
const searchIconSx = { width: 18, height: 18, display: "block", objectFit: "contain" };
const promptPanelsStackSx = { gap: { xs: 2, md: "17px" }, mt: { xs: 0, md: "3px" } };
const promptPanelHeaderSx = {
  minHeight: 54,
  px: 3,
  pt: 3,
  pb: 1,
  gap: 1,
  alignItems: { xs: "flex-start", sm: "center" },
  flexWrap: "wrap",
  boxSizing: "border-box",
};
const promptHistoryHeaderSx = { minHeight: 54, px: 3, pt: 3, pb: 1, gap: 1, alignItems: "center", justifyContent: "flex-start", boxSizing: "border-box", textAlign: "left" };
const promptPanelContentSx = { p: 3, gap: 2, alignItems: "stretch", boxSizing: "border-box" };
const promptEditorGroupSx = { display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1, width: "100%" };
const promptInstructionGroupSx = { display: "flex", flexDirection: "column", alignItems: "stretch", gap: "4px", width: "100%" };
const promptFieldLabelSx = { color: "#2E3341", fontFamily: figmaFontFamily, fontSize: 13, fontWeight: 600, lineHeight: "20px" };
const promptTextFieldSx = (expanded: boolean) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    height: expanded ? "auto" : 181,
    minHeight: expanded ? 720 : 181,
    p: "24px 32px 24px 24px",
    borderRadius: "8px",
    fontFamily: figmaFontFamily,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    alignItems: "flex-start",
    bgcolor: "#fff",
  },
  "& .MuiInputBase-inputMultiline": {
    p: 0,
    height: expanded ? "auto" : "132px !important",
    overflowY: "auto !important",
    color: "#2E3341",
    fontFamily: figmaFontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "-0.015em",
    scrollbarColor: "#A8A8A8 transparent",
    scrollbarGutter: "stable",
    scrollbarWidth: "thin",
  },
  "& .MuiInputBase-inputMultiline::-webkit-scrollbar": { width: "6px", height: "6px" },
  "& .MuiInputBase-inputMultiline::-webkit-scrollbar-track": { bgcolor: "transparent", my: "8px" },
  "& .MuiInputBase-inputMultiline::-webkit-scrollbar-thumb": { bgcolor: "#A8A8A8", borderRadius: "3px", minHeight: "63px" },
  "& .MuiInputBase-inputMultiline::-webkit-scrollbar-corner": { bgcolor: "transparent" },
  "& textarea::placeholder": { color: "#A3A3A3", opacity: 1 },
  "& fieldset": { borderColor: "#DDE0EA" },
  "& .MuiInputBase-root:hover fieldset": { borderColor: "#C8CDD9" },
});
const promptUpdatedSx = { color: "#6F7279", fontFamily: figmaFontFamily, fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const promptEditorShellSx = { position: "relative", width: "100%" };
const promptExpandButtonSx = {
  position: "absolute",
  right: 16,
  bottom: 16,
  width: 68,
  height: 40,
  minWidth: 68,
  p: "0 12px",
  borderRadius: "4px",
  color: "#2E3341",
  bgcolor: "#fff",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)",
  fontFamily: figmaFontFamily,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  textTransform: "none",
  "& .MuiButton-startIcon": { mr: "4px" },
  "&:hover": { bgcolor: "#fff", boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)" },
};
const promptExpandIconSx = { width: 16, height: 16, display: "block", objectFit: "contain" };
const promptCounterSx = { display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%", height: 20, mt: 0, fontFamily: figmaFontFamily, fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const promptCounterCurrentSx = { color: "#2E3341" };
const promptCounterLimitSx = { color: "#6F7279" };
const promptNoteGroupSx = { display: "flex", flexDirection: "column", alignItems: "stretch", gap: "4px", width: "100%" };
const promptNoteFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": { height: 48, borderRadius: "8px", fontFamily: figmaFontFamily, fontSize: 16, fontWeight: 400, lineHeight: "24px", bgcolor: "#fff" },
  "& input": { color: "#2E3341", letterSpacing: "-0.015em", py: 0 },
  "& input::placeholder": { color: "#A3A3A3", opacity: 1 },
  "& fieldset": { borderColor: "#DDE0EA" },
  "& .MuiInputBase-root:hover fieldset": { borderColor: "#C8CDD9" },
};
const promptInfoSx = { display: "flex", alignItems: "center", gap: "4px", minWidth: 0, borderRadius: "8px" };
const promptInfoIconSx = { width: 16, height: 16, borderRadius: "16px", bgcolor: "#0D67DD", color: "#fff", fontSize: 11, fontWeight: 700, lineHeight: "16px", textAlign: "center", flexShrink: 0 };
const promptInfoTextSx = { color: "#0D67DD", fontFamily: figmaFontFamily, fontSize: 13, fontWeight: 500, lineHeight: "20px", letterSpacing: "-0.015em" };
const promptSaveButtonSx = { ...actionButtonMetricsSx, width: 126, minWidth: 126, borderRadius: "8px", bgcolor: "#2E3341", color: "#fff", fontFamily: figmaFontFamily, fontWeight: 600, letterSpacing: "-0.015em", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#252A35", boxShadow: "none" }, "&.Mui-disabled": { bgcolor: "#C5C8D0", color: "#fff" } };
const promptHistoryContentSx = { p: 3, gap: 3, alignItems: "stretch", boxSizing: "border-box" };
const promptHistoryGridSx = { display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 2, width: "100%" };
const promptHistoryCardSx = { minHeight: 120, p: 2, borderRadius: "8px", border: "1px solid #DDE0EA", bgcolor: "#FAFAFA", display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "space-between", gap: 2, minWidth: 0, boxSizing: "border-box" };
const promptHistoryTitleSx = { color: "#000", fontFamily: figmaFontFamily, fontSize: 14, fontWeight: 600, lineHeight: "22px" };
const promptHistoryMetaSx = { color: "#6F7279", fontFamily: figmaFontFamily, fontSize: 12, fontWeight: 400, lineHeight: "18px", mt: 0 };
const promptHistoryActionSx = { display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%", minWidth: 0 };
const promptHistoryButtonSx = { ...outlineButtonSx, width: 99, minWidth: 99, height: 32, minHeight: 32, px: 1.5, flexShrink: 0, boxSizing: "border-box", fontFamily: figmaFontFamily, whiteSpace: "nowrap", wordBreak: "keep-all", overflow: "hidden" };
const promptHistoryButtonLabelSx = { display: "inline-block", whiteSpace: "nowrap", wordBreak: "keep-all", lineHeight: "14px" };
const promptDialogBackdropSx = { bgcolor: "rgba(0, 0, 0, 0.4)" };
const promptDialogPaperSx = { width: 780, maxWidth: "calc(100vw - 32px)", height: { xs: "calc(100vh - 32px)", md: 786 }, maxHeight: "calc(100vh - 32px)", borderRadius: "24px", bgcolor: "#fff", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)", m: 0, overflow: "hidden" };
const promptDialogContentSx = { p: 0, display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: figmaFontFamily };
const promptDialogHeaderSx = { display: "flex", alignItems: "flex-start", width: "100%", height: 90, px: 5, pt: 5, pb: 0, gap: "7px", boxSizing: "border-box", flexShrink: 0 };
const promptDialogTitleBlockSx = { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", width: { xs: "100%", md: 303 }, minWidth: 0 };
const promptDialogTitleSx = { width: "100%", color: "#000", fontFamily: figmaFontFamily, fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const promptDialogMetaSx = { width: "100%", color: "#6F7279", fontFamily: figmaFontFamily, fontSize: 13, fontWeight: 400, lineHeight: "20px" };
const promptDialogBodyWrapSx = { width: "100%", height: 608, p: "24px 40px", boxSizing: "border-box", flexShrink: 1, minHeight: 0 };
const promptDialogBodySx = {
  width: "100%",
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  p: "16px 32px 16px 16px",
  bgcolor: "#fff",
  boxSizing: "border-box",
  scrollbarColor: "#A8A8A8 transparent",
  scrollbarGutter: "stable",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
  "&::-webkit-scrollbar-track": { bgcolor: "transparent", my: "8px" },
  "&::-webkit-scrollbar-thumb": { bgcolor: "#A8A8A8", borderRadius: "3px", minHeight: "195px" },
  "&::-webkit-scrollbar-corner": { bgcolor: "transparent" },
};
const promptDialogPreSx = { m: 0, width: "100%", color: "#2E3341", fontFamily: figmaFontFamily, fontSize: 16, fontWeight: 400, lineHeight: "24px", letterSpacing: "-0.015em", whiteSpace: "pre-wrap", wordBreak: "break-word" };
const promptDialogActionsSx = { width: "100%", height: 88, px: 5, pb: 5, gap: 1, justifyContent: "flex-end", alignItems: "flex-start", boxSizing: "border-box", flexShrink: 0 };
const promptDialogCancelButtonSx = { ...actionButtonMetricsSx, width: 80, minWidth: 80, borderRadius: "8px", color: "#2E3341", border: "1px solid #DDE0EA", bgcolor: "#fff", fontFamily: figmaFontFamily, fontWeight: 600, letterSpacing: "-0.015em", textTransform: "none", boxShadow: "none", "&:hover": { borderColor: "#C8CDD9", bgcolor: "#fff", boxShadow: "none" }, "&.Mui-disabled": { color: "#A3A3A3", borderColor: "#E1E4EC", bgcolor: "#fff" } };
const promptDialogRestoreButtonSx = { ...actionButtonMetricsSx, width: 157, minWidth: 157, borderRadius: "8px", bgcolor: "#2E3341", color: "#fff", fontFamily: figmaFontFamily, fontWeight: 600, letterSpacing: "-0.015em", textTransform: "none", boxShadow: "none", whiteSpace: "nowrap", "&:hover": { bgcolor: "#252A35", boxShadow: "none" }, "&.Mui-disabled": { bgcolor: "#C5C8D0", color: "#fff" } };
const figmaRadioSx = { width: 16, height: 16, p: 0, color: "transparent", "&:hover": { bgcolor: "transparent" } };
const radioEmptyIconSx = { width: 16, height: 16, borderRadius: "50%", bgcolor: "#D4D4D4", display: "block" };
const radioCheckedIconSx = { ...radioEmptyIconSx, bgcolor: "#5D88F1", position: "relative" };
const radioCheckedDotSx = { position: "absolute", left: 4, top: 4, width: 8, height: 8, borderRadius: "50%", bgcolor: "#fff" };
const chipDeleteIconSx = { position: "relative", width: 24, height: 24, flex: "0 0 auto", opacity: 0.6, borderRadius: "12px", display: "block", cursor: "pointer" };
const chipDeleteLineSx = (deg: number) => ({ position: "absolute", left: 8, top: 11, width: 11.31, height: 0, borderTop: "1.5px solid #DE3434", color: "#DE3434", transform: `rotate(${deg}deg)` });
const keywordDialogPaperSx = { width: 560, maxWidth: "calc(100vw - 32px)", borderRadius: "24px", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)", overflow: "hidden", m: 2 };
const keywordDialogTitleSx = { px: 5, pt: 5, pb: 0, minHeight: 66, boxSizing: "border-box", color: "#000", fontSize: 20, fontWeight: 600, lineHeight: "26px" };
const keywordDialogContentSx = { px: 5, py: 3, display: "flex", flexDirection: "column", gap: 3, overflow: "visible" };
const keywordDialogActionsSx = { px: 5, pb: 5, pt: 0, gap: 1, height: 80, boxSizing: "border-box", alignItems: "flex-start", justifyContent: "flex-end" };
const keywordDialogFieldGroupSx = { width: "100%", display: "flex", flexDirection: "column", gap: 0.5 };
const keywordDialogFieldLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px" };
const keywordDialogRadioGroupSx = { gap: 3, height: 40, alignItems: "center" };
const keywordDialogRadioLabelSx = {
  m: 0,
  gap: 1,
  height: 40,
  color: "#2E3341",
  "& .MuiFormControlLabel-label": { color: "#2E3341", fontSize: 16, fontWeight: 500, lineHeight: "24px", letterSpacing: "-0.015em" },
  "&.Mui-disabled .MuiFormControlLabel-label": { color: "#2E3341" },
};
const keywordDialogTextFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": { height: 48, minHeight: 48, borderRadius: "8px", bgcolor: "#fff", boxSizing: "border-box", px: 2, fontSize: 16, lineHeight: "24px", letterSpacing: "-0.015em" },
  "& .MuiInputBase-input": { height: 24, p: 0, color: "#2E3341", lineHeight: "24px", "&::placeholder": { color: "#A3A3A3", opacity: 1 } },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#C8CDD9" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2E3341" },
};
const keywordDialogExcludeSectionSx = { width: "100%", display: "flex", flexDirection: "column", gap: 1.5 };
const keywordDialogExcludeHeaderSx = { width: "100%", minHeight: 24 };
const keywordDialogCountTagSx = { minWidth: 36, height: 24, px: 1, borderRadius: "16px", bgcolor: "#EEEFF0", color: "#2E3341", fontSize: 13, fontWeight: 500, lineHeight: "13px", display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box" };
const keywordDialogExcludeInputRowSx = { width: "100%", gap: 0.5, height: 48, alignItems: "flex-start" };
const keywordDialogExcludeAddButtonSx = {
  width: 88,
  minWidth: 88,
  height: 48,
  borderRadius: "8px",
  border: "1px solid #2E3341",
  bgcolor: "#fff",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  letterSpacing: "-0.015em",
  boxShadow: "none",
  gap: 1,
  "& .MuiButton-startIcon": { m: 0, "& svg": { width: 16, height: 16, strokeWidth: 2 } },
  "&:hover": { bgcolor: "#FAFAFA", borderColor: "#2E3341", boxShadow: "none" },
  "&.Mui-disabled": { color: "#A3A3A3", borderColor: "#DDE0EA", bgcolor: "#fff" },
};
const keywordDialogExcludeWellSx = (hasTerms: boolean) => ({
  width: "100%",
  minHeight: hasTerms ? 64 : 52,
  p: 2,
  borderRadius: "8px",
  border: "1px dashed #DDE0EA",
  bgcolor: "#FAFAFA",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: hasTerms ? "flex-start" : "center",
});
const keywordDialogExcludeChipListSx = { width: "100%", flexWrap: "wrap", gap: 1, alignItems: "center", alignContent: "flex-start" };
const keywordDialogExcludeChipSx = {
  height: 32,
  borderRadius: "16px",
  bgcolor: "#EAEAEA",
  color: "#2E3341",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "14px",
  "& .MuiChip-label": { px: 1.5, pr: 0.5 },
  "& .MuiChip-deleteIcon": { m: 0, mr: 0.5 },
};
const keywordDialogExcludeEmptySx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const keywordDialogEnabledRowSx = { gap: 1, width: "auto", height: 24 };
const keywordDialogDisabledLabelSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "22px", letterSpacing: "-0.015em" };
const keywordDialogEnabledLabelSx = { ...keywordDialogDisabledLabelSx, color: "#2E3341" };
const keywordDialogCancelButtonSx = { ...actionButtonMetricsSx, width: 80, minWidth: 80, borderRadius: "8px", color: "#2E3341", border: "1px solid #DDE0EA", bgcolor: "#fff", fontWeight: 600, letterSpacing: "-0.015em", textTransform: "none", boxShadow: "none", "&:hover": { borderColor: "#C8CDD9", bgcolor: "#fff", boxShadow: "none" }, "&.Mui-disabled": { color: "#A3A3A3", borderColor: "#E1E4EC", bgcolor: "#fff" } };
const keywordDialogPrimaryButtonSx = (editMode?: boolean) => ({ ...actionButtonMetricsSx, width: editMode ? 80 : 104, minWidth: 80, borderRadius: "8px", bgcolor: "#2E3341", color: "#fff", fontWeight: 600, letterSpacing: "-0.015em", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#252A35", boxShadow: "none" }, "&.Mui-disabled": { bgcolor: "#C5C8D0", color: "#fff" } });
const dialogTitleSx = { display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 3, sm: 5 }, pt: { xs: 3, sm: 4 }, pb: 2 };
const dialogTitleTextSx = { color: "#111827", fontSize: 20, fontWeight: 700 };

export default KeywordConfigPage;
